import {
  getApiClientErrorBody,
  getApiClientErrorDetail,
  getApiClientErrorMessageFromError,
  getApiClientErrorStatus,
  hydraUiApi,
} from '@/services/api';

import { AiEnabledGuard } from '@/components/guards';
import { authStore } from '@/services/auth/store';
import {
  CopilotActiveExchangeError,
  setCopilotConfig,
  type APICopilotGetFileDownloadUrl,
  type APICopilotGetUploadSignature,
  type CopilotMessage,
  type CopilotUploadSignatureResponse,
  type ServerSideCopilotToolPayload,
} from '@customerio/hydra-copilot';
import { useSuppressSidebar } from '@/features/sidebar';
import { invalidateAgentUsageMeter } from '@/features/billing-usage';
import { SidebarChat } from './chat';
import { History } from './history';
import { processStreamingLines } from './helpers/process-streaming-lines';
import { useIsAgentFullscreen } from './hooks';
import { resolveCopilotWorkspaceId } from './workspace-context';

setCopilotConfig({
  async startSession(model?: string) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.create({
      params: { path: { account_id: String(accountId) } },
      // The generated client types `body` from a required requestBody, and this one is optional,
      // so a typed body resolves to never. Raw until OperationRequestBody handles both.
      // Omitted when nothing was picked, so an account without the picker sends what it always did.
      rawBody: JSON.stringify(model == null || model === '' ? {} : { model }),
      headers: { 'Content-Type': 'application/json' },
    });

    return {
      id: String(response.session_id),
      // `tools` is modeled as an untyped JSON array in the spec; narrow it to
      // the server-side tool payload shape the copilot package expects.
      tools:
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        response.tools as ServerSideCopilotToolPayload[],
    };
  },
  getUploadSignature: (async (
    payload: Parameters<APICopilotGetUploadSignature>[0],
  ): Promise<CopilotUploadSignatureResponse> => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    try {
      const response = await hydraUiApi.copilot.sign({
        params: {
          path: {
            account_id: String(accountId),
            session_id: payload.id,
          },
        },
        body: {
          size: payload.size,
          mime_type: payload.mime_type,
        },
      });

      // The signed-upload shape is identical at runtime; the generated GCS
      // signature type is looser (optional/nullable map) than the copilot
      // package's, so narrow it at this boundary.
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      return response as CopilotUploadSignatureResponse;
    } catch (error) {
      const bodyMessage = getApiClientErrorMessageFromError(error);
      if (bodyMessage != null) {
        throw new Error(bodyMessage, { cause: error });
      }
      throw error;
    }
  }) satisfies APICopilotGetUploadSignature,
  getFileDownloadUrl: (async (payload: Parameters<APICopilotGetFileDownloadUrl>[0]) => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.getFiles({
      params: {
        path: {
          account_id: String(accountId),
          session_id: payload.id,
          filename: payload.filename,
        },
      },
    });

    return {
      url: response.url,
    };
  }) satisfies APICopilotGetFileDownloadUrl,
  async getSession(id) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.get({
      params: { path: { account_id: String(accountId), session_id: String(id) } },
    });

    return {
      meta: { name: response.name, model: response.model ?? '' },
      // Generated message/tool models are structurally looser than the copilot
      // package's union types; narrow them at this boundary.
      messages:
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        response.messages as CopilotMessage[],
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      tools: response.tools as ServerSideCopilotToolPayload[],
    };
  },
  async sendStreamingMessage({ onMessage, id, signal, ...payload }) {
    const state = { currentLine: -1 };
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    try {
      // The streaming exchange returns newline-delimited JSON. Its request body
      // and stream response aren't modeled in the spec, so send a raw JSON body
      // and read the raw Response. `timeout: 0` disables the client's default
      // request timeout — an exchange is long-lived and must not be aborted
      // mid-stream (the caller's `signal` still cancels it).
      const response = await hydraUiApi.copilot.createBySession({
        params: { path: { account_id: String(accountId), session_id: id } },
        rawBody: JSON.stringify({
          environment_id: resolveCopilotWorkspaceId(),
          stream: true,
          ...payload,
        }),
        headers: { 'Content-Type': 'application/json' },
        signal,
        timeout: 0,
        responseType: 'response',
      });

      const body = response.body;
      if (body == null) {
        return;
      }

      const reader = body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      // Re-run the line processor over the accumulated text on each chunk;
      // `state.currentLine` skips already-emitted lines and leaves a trailing
      // partial line for the next chunk.
      for (;;) {
        const { done, value } = await reader.read();
        if (value != null) {
          accumulated += decoder.decode(value, { stream: true });
          processStreamingLines(accumulated, state, onMessage);
        }
        if (done) {
          break;
        }
      }

      // Flush any bytes buffered in the decoder and process the final lines.
      accumulated += decoder.decode();
      processStreamingLines(accumulated, state, onMessage);
    } catch (error) {
      if (getApiClientErrorStatus(error) === 409) {
        throw new CopilotActiveExchangeError('The agent is still working on the previous turn.', { cause: error });
      }

      // Only rewrap when the error body carries a detail to surface; pass
      // cancels, timeouts, network failures, and body-less HTTP errors through
      // unchanged so downstream isAbortError / status checks / retry logic still
      // works. (getApiClientErrorMessageFromError would synthesize a generic
      // "HTTP code N" message here, masking the original client error.)
      const bodyMessage = getApiClientErrorDetail(getApiClientErrorBody(error));
      if (bodyMessage != null) {
        throw new Error(bodyMessage, { cause: error });
      }
      throw error;
    } finally {
      // An exchange can burn enough tokens to cross a usage threshold, and the
      // meter query only refetches on mount otherwise. Refresh it whether the
      // exchange finished, failed, or was stopped — tokens were spent either
      // way. Fire-and-forget: the meter must never affect the exchange result.
      void invalidateAgentUsageMeter();
    }
  },
  async stopExchange({ id }) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await hydraUiApi.copilot.stop({
      params: { path: { account_id: String(accountId), session_id: id } },
    });
  },
  async endSession() {
    // noop
  },
  async generate(payload) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    return await hydraUiApi.copilotGenerate.create({
      params: { path: { account_id: String(accountId) } },
      body: {
        ...payload,
        // null → 0; the backend decodes both to int 0 and skips the env-access
        // check, matching the previous untyped behavior.
        environment_id: resolveCopilotWorkspaceId() ?? 0,
      },
    });
  },
});

export function Copilot() {
  const isAgentFullscreen = useIsAgentFullscreen();
  // Hide (not close) the panel while fullscreen so the sidebar comes back open
  // when the user navigates back out — e.g. Agent → Skills → back.
  useSuppressSidebar('copilot', isAgentFullscreen);
  // Both panels live in this component, so both would otherwise leave an empty gutter.
  useSuppressSidebar('copilot-history', isAgentFullscreen);

  if (isAgentFullscreen) {
    return null;
  }

  return (
    <>
      <AiEnabledGuard>
        <History />
      </AiEnabledGuard>
      <SidebarChat />
    </>
  );
}

export { CopilotInput, CopilotMessages, CopilotName, CopilotSupportButton } from './chat';
export { AgentBetaLabel } from './components/agent-beta-label';
export { ModelPicker } from './components/chat-messages/model-picker';
export { AiGradientSparkleBadge } from './components/ai-gradient-sparkle-badge';
export { AiGradientSparkleIcon } from './components/ai-gradient-sparkle-icon';
export {
  CopilotSessionProvider,
  useCopilotConversations,
  useCopilotSessionContext,
} from './components/session-provider';
export { AssistantFooter } from './components/assistant-footer';
export { AgentLauncher } from './components/agent-launcher';
export { useSuppressAgentLauncher } from './hooks/use-suppress-agent-launcher';
export {
  CopilotSidebarProvider,
  useCopilotSidebar,
  useIsAgentFullscreen,
  useIsAwaitingFirstUserAnswer,
  useNavigateToAgentChat,
} from './hooks';
export { Chats as CopilotChats, ChatCountBar as CopilotChatCountBar } from './history';
export { useCopilotHistory } from './hooks';
export { useMasqAccessLevel } from './hooks/use-masq-access-level';
export { GlobalCopilotConfiguration } from './global-copilot-configuration';
